'use client';

import { ContentClassEnum } from 'content-service-client';
import { usePathname } from 'next/navigation';
import { JSX } from 'react';

import { Button, Text } from '@videoblocks/design-system';

import { CollectionViewItem } from './CollectionViewItem';
import { AUDIO_COLLECTION_ITEMS, IMAGE_COLLECTION_ITEMS, VIDEO_COLLECTION_ITEMS } from './constants';

export interface NotFoundSearchResultsProps {
    contentClass: ContentClassEnum;
}

export const getCollectionDataFromContentClass = (
    contentClass: ContentClassEnum,
): {
    collectionItems: { thumbnailUrl: string; url: string; name: string }[];
    headerText: string;
    collectionText: string;
} => {
    switch (contentClass) {
        case ContentClassEnum.Video:
            return {
                collectionItems: VIDEO_COLLECTION_ITEMS,
                headerText: 'videos',
                collectionText: 'videos',
            } as const;
        case ContentClassEnum.Audio:
            return {
                collectionItems: AUDIO_COLLECTION_ITEMS,
                headerText: 'audio',
                collectionText: 'tracks',
            } as const;
        case ContentClassEnum.Image:
            return {
                collectionItems: IMAGE_COLLECTION_ITEMS,
                headerText: 'images',
                collectionText: 'images',
            } as const;
        default:
            return {
                collectionItems: [],
                headerText: 'content',
                collectionText: 'content',
            };
    }
};

export const NotFoundSearchResults = ({ contentClass }: NotFoundSearchResultsProps): JSX.Element => {
    const pathname = usePathname();
    const { collectionItems, collectionText, headerText } = getCollectionDataFromContentClass(contentClass);

    return (
        <div className="w-full">
            <div className="flex flex-col gap-3 p-3 bg-gray-100">
                <div>
                    <Text fontSize="lg" fontWeight="regular" asChild>
                        <h5>Sorry, we couldn&rsquo;t find any {headerText} that match your selected criteria</h5>
                    </Text>
                    <Text fontSize="sm" fontWeight="regular">
                        Here are some tips to help you find what you need:
                    </Text>
                </div>
                <ul className="px-7 gap-1 flex flex-col">
                    <Text asChild fontSize="sm" fontWeight="regular">
                        <li>Try a new search term</li>
                    </Text>
                    <Text asChild fontSize="sm" fontWeight="regular">
                        <li>Check your spelling</li>
                    </Text>
                    <Text asChild fontSize="sm" fontWeight="regular">
                        <li>Clear your filters</li>
                    </Text>
                </ul>
                <Button title="Remove Filters" href={pathname} className="w-fit">
                    Remove Filters
                </Button>
            </div>
            <div className="flex flex-col gap-3 mt-6 mx-3">
                <Text asChild fontSize="lg" fontWeight="regular">
                    <h5>Try checking out some of our Collections</h5>
                </Text>
                <Text fontWeight="regular">
                    We handpicked some of the freshest, hottest {collectionText} for you. Spend less time searching,
                    more time creating.
                </Text>
                <div className="w-full grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-3 pb-13 pt-4">
                    {collectionItems.map((collection, index) => (
                        <CollectionViewItem
                            thumbnailUrl={collection.thumbnailUrl}
                            url={collection.url}
                            name={collection.name}
                            key={index}
                        />
                    ))}
                </div>
            </div>
        </div>
    );
};
